import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";
import { queryOptions } from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    createResourceSelector,
    handleJsonApiError,
} from "jsonapi-zod-query";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { z } from "zod";
import { zj } from "zod-joda";

const bidderSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string(),
    phoneNumber: z.string().transform((value) => parsePhoneNumberWithError(value, "US")),
    streetAddress: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    attendance: z.enum(["in_person", "zoom"]).nullable(),
});

const optionalContactSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string().nullable(),
    phoneNumber: z
        .string()
        .transform((value) => parsePhoneNumberWithError(value, "US"))
        .nullable(),
    streetAddress: z.string(),
    city: z.string(),
    state: z.string().nullable(),
    zipCode: z.string(),
});

const openingBidSchema = z.object({
    target: z.enum(["parcel", "parcel_combination", "global"]),
    index: z.number(),
    amount: z.number(),
});

export const registrationAttributesSchema = z.object({
    registrationNumber: z.number(),
    createdAt: zj.zonedDateTime().transform((value) => value.withZoneSameInstant(localZoneId)),
    bidder: bidderSchema,
    attorney: optionalContactSchema.extend({
        lawFirm: z.string().trim(),
    }),
    lender: optionalContactSchema.extend({
        financialInstitution: z.string().trim(),
    }),
    openingBids: z.array(openingBidSchema),
});

const registrationSelector = createDataSelector(
    createResourceSelector({
        type: "registration",
        attributesSchema: registrationAttributesSchema,
    }),
);

const registrationsSelector = createDataSelector(
    createResourceCollectionSelector({
        type: "registration",
        attributesSchema: registrationAttributesSchema
            .pick({
                registrationNumber: true,
                createdAt: true,
                bidder: true,
            })
            .extend({
                openingBids: z.array(openingBidSchema).optional(),
            }),
    }),
);

export type Registration = ReturnType<typeof registrationSelector>;
export type ListRegistration = ReturnType<typeof registrationsSelector>[number];

type ListOptions = {
    includeOpeningBids: boolean;
};

export const createRegistrationQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (auctionId: string, options?: ListOptions) =>
        queryOptions({
            queryKey: ["auction", auctionId, "registrations", options],
            queryFn: async ({ signal }) => {
                const fields = ["registrationNumber", "createdAt", "bidder"];

                if (options?.includeOpeningBids) {
                    fields.push("openingBids");
                }

                const url = apiUrl(`/auctions/${auctionId}/registrations`);
                url.searchParams.set("fields[registration]", fields.join(","));

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return registrationsSelector(await response.json());
            },
        }),
    get: (auctionId: string, registrationId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId, "registration", registrationId],
            queryFn: async ({ signal }) => {
                const response = await authFetch(
                    apiUrl(`/auctions/${auctionId}/registrations/${registrationId}`),
                    { signal },
                );
                await handleJsonApiError(response);
                return registrationSelector(await response.json());
            },
        }),
});
